// Chart.js integration. Chart.js is BUNDLED into dist/gv-financials.js — the
// embedding page adds one script tag and nothing else. Charts are progressive
// enhancement: the accompanying tables are the accessible source of truth, so
// if a canvas 2D context is unavailable (e.g. DOM test environments) chart
// creation degrades silently and the tables still render.

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartConfiguration,
} from "chart.js";
import { h } from "./dom.js";
import {
  CHART_GRID,
  CHART_LINE,
  CHART_SERIES_A,
  CHART_SERIES_B,
  CHART_TICK,
  FONT_BODY,
} from "./styles.js";

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
);

export interface BarSeries {
  label: string;
  values: number[];
  color: string;
}

export interface LineSeries {
  label: string;
  values: number[];
}

const AXIS_FONT = { family: FONT_BODY, size: 11 } as const;

/**
 * Append a chart block (canvas in a fixed-height wrapper) to `container` and
 * try to render a grouped bar chart with an optional overlay line. The block
 * is appended BEFORE the Chart is constructed so Chart.js can measure real
 * dimensions (a detached canvas measures 0x0). Returns the Chart instance
 * (null when the environment cannot draw — the tables carry the data).
 */
export function barChartBlock(
  container: HTMLElement,
  labels: string[],
  bars: BarSeries[],
  line: LineSeries | null,
  ariaLabel: string,
): Chart | null {
  const canvas = h("canvas", { role: "img", "aria-label": ariaLabel });
  const wrap = h("div", { class: "gvf-chart-wrap" }, [canvas]);
  const element = h("div", { class: "gvf-chart-block" }, [wrap]);
  container.append(element);

  const datasets: ChartConfiguration<"bar" | "line">["data"]["datasets"] = bars.map(
    (series) => ({
      type: "bar" as const,
      label: series.label,
      data: series.values,
      backgroundColor: series.color,
      borderRadius: 4,
      borderSkipped: "start" as const,
      maxBarThickness: 48,
      categoryPercentage: 0.6,
      barPercentage: 0.85,
    }),
  );
  if (line) {
    datasets.push({
      type: "line" as const,
      label: line.label,
      data: line.values,
      borderColor: CHART_LINE,
      backgroundColor: CHART_LINE,
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0,
    });
  }

  const config: ChartConfiguration<"bar" | "line"> = {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: datasets.length > 1,
          position: "bottom",
          labels: { font: AXIS_FONT, color: CHART_TICK, boxWidth: 12, boxHeight: 12 },
        },
        tooltip: { enabled: true },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: AXIS_FONT, color: CHART_TICK },
        },
        y: {
          grid: { color: CHART_GRID },
          border: { display: false },
          ticks: { font: AXIS_FONT, color: CHART_TICK },
        },
      },
    },
  };

  try {
    return new Chart(canvas, config);
  } catch {
    // No canvas 2D context (test env / ancient browser): tables carry the data.
    element.remove();
    return null;
  }
}

export const SERIES_COLORS = { a: CHART_SERIES_A, b: CHART_SERIES_B } as const;
export type { Chart };
